import React, { useCallback, useEffect, useRef } from 'react';

import { WIZARD_API, useWizardApiRegistry } from '@/features/walkthrough/demo/wizardApi';

import { ASSISTANT_EVENT, useSparkAssistant } from '../hooks/useSparkAssistant';
import { applyAssistantActions, isUploadOption, openNativeFilePicker } from '../utils/assistantActions';
import SparkAssistantPanel from './SparkAssistantPanel';

/** How often the wizard is sampled for edits the assistant should hear about. */
export const WIZARD_ASSISTANT_POLL_MS = 1000;

/**
 * Builds the `WizardState` the backend scripts read (see
 * `backend/app/services/assistant/models.py`). Step snapshots come from the
 * registered step apis so this stays correct whichever step is mounted.
 */
export const buildWizardState = ({ step, form, usecaseId, isSaas, registry }) => ({
    step,
    form: form || {},
    usecase_id: usecaseId ?? null,
    is_saas: Boolean(isSaas),
    // Empty objects (not null): the backend schema expects a step snapshot
    // object, and a step whose page is not mounted yet has nothing to report.
    data: registry?.get(WIZARD_API.dataCollection)?.getSnapshot?.() ?? {},
    model_setup: registry?.get(WIZARD_API.modelSetup)?.getSnapshot?.() ?? {},
});

/**
 * Spark panel wired to the live wizard: must render inside the
 * `WizardApiProvider` the wizard page registers its step apis with.
 *
 * - `step_entered` is sent whenever `step` changes (after the greeting).
 * - `wizard_changed` is sent when the sampled `WizardState` differs from the
 *   last one sent (form edits, uploads, teacher picks …); the hook debounces it.
 * - Backend actions are applied through the registry (`applyAssistantActions`).
 */
export default function WizardAssistant({
    step,
    form,
    usecaseId,
    isSaas = false,
    user,
    onClose,
    className = '',
    pollMs = WIZARD_ASSISTANT_POLL_MS,
    timings,
}) {
    const registry = useWizardApiRegistry();

    const latestRef = useRef({ step, form, usecaseId, isSaas });
    latestRef.current = { step, form, usecaseId, isSaas };

    const getWizardState = useCallback(
        () => buildWizardState({ ...latestRef.current, registry }),
        [registry]
    );

    // The file dialog only opens inside the click that asked for it. Opening
    // it after the chat request returns is no longer a user gesture, so the
    // browser drops it and the promise never settles — which left the chat
    // unable to answer anything typed afterwards.
    const pendingPickerRef = useRef(null);
    const applyActions = useCallback(
        (actions) =>
            applyAssistantActions(actions, registry, {
                pickFiles: (opts) => {
                    const pending = pendingPickerRef.current;
                    if (pending) {
                        pendingPickerRef.current = null;
                        return pending;
                    }
                    return openNativeFilePicker(opts);
                },
            }),
        [registry]
    );

    const assistant = useSparkAssistant({ getWizardState, applyActions, timings });
    const { notifyEvent, selectOption: commitOption } = assistant;

    const selectOption = useCallback(
        (option) => {
            if (isUploadOption(option?.id)) {
                const dataCollection = registry?.get(WIZARD_API.dataCollection);
                pendingPickerRef.current = openNativeFilePicker({ accept: dataCollection?.accept });
            }
            commitOption(option);
        },
        [commitOption, registry]
    );

    const serializeState = useCallback(() => {
        try {
            return JSON.stringify(getWizardState());
        } catch {
            return null;
        }
    }, [getWizardState]);

    // Step changes are reported immediately. The first render is the
    // greeting's job, so it is skipped here. The sampled state is re-based so
    // the step turn is not followed by a redundant `wizard_changed`.
    const lastStateRef = useRef(null);
    const lastStepRef = useRef(step);
    useEffect(() => {
        if (lastStepRef.current === step) return;
        lastStepRef.current = step;
        lastStateRef.current = serializeState();
        notifyEvent(ASSISTANT_EVENT.stepEntered, {});
    }, [notifyEvent, serializeState, step]);

    // Wizard edits: the step apis have no change feed, so the state is sampled
    // and compared; `useSparkAssistant` coalesces the resulting events.
    useEffect(() => {
        const sample = () => {
            const serialized = serializeState();
            if (serialized === null) return;
            if (lastStateRef.current === null) {
                lastStateRef.current = serialized;
                return;
            }
            if (serialized === lastStateRef.current) return;
            lastStateRef.current = serialized;
            notifyEvent(ASSISTANT_EVENT.wizardChanged, {});
        };
        sample();
        if (!pollMs) return undefined;
        const timer = setInterval(sample, pollMs);
        return () => clearInterval(timer);
    }, [serializeState, notifyEvent, pollMs, step, form, usecaseId]);

    return (
        <SparkAssistantPanel
            className={className}
            messages={assistant.messages}
            latestAssistantIndex={assistant.latestAssistantIndex}
            loading={assistant.loading}
            onSend={assistant.sendMessage}
            onSelectOption={selectOption}
            onClose={onClose}
            user={user}
        />
    );
}
